import React, { useMemo, useState } from "react";
import S from "./strings";
import { isRtlLanguage } from "./locale";
import useUiLanguage from "./useUiLanguage";
import { coachingPersonalityService } from "../../../services/coachingPersonalityApi";

// COACHING PERSONALITY — the intro and the fifty statements.
//
// Lives INSIDE the profile section, not on a route of its own: Konzept 4.1 says
// the section "is the home of the feature ... everything in the section". The
// prototype draws these two screens full width because a prototype has no
// profile page around them; the measure it draws them at (620px) is kept, so
// the reading line is the prototype's.
//
// PAUSE AND RESUME IS SERVER STATE, NOT COMPONENT STATE. Nothing in this repo
// had it before (the registration steps keep no draft at all), so it is built
// on the draft endpoints: every answer is PATCHed as it is given, and PATCH
// MERGES server side, so question 41 never erases questions 1 to 40. Pause and
// Finish additionally send the whole local map, which is idempotent and repairs
// any single write that was lost on a bad connection.
//
// The draft is invisible by construction: it holds no sums, it is served to
// nobody but its owner, and the sentence under the progress bar says so on
// every screen (Konzept 4.2).

const PRIMARY =
  "min-h-[44px] px-6 rounded-[8px] border-none bg-[#2183FE] text-white text-[14px] font-semibold";
const GHOST =
  "min-h-[44px] px-[10px] bg-transparent border-none text-[#6B7280] text-[13px] hover:text-[#111827]";

const Questionnaire = ({
  questionnaire,
  onPause,
  onCompleted,
  onLeaveIntro,
  startOnIntro = true,
}) => {
  const language = useUiLanguage();
  const rtl = isRtlLanguage(language);
  const statements = useMemo(
    () => questionnaire?.statements || [],
    [questionnaire]
  );
  const likert = useMemo(() => questionnaire?.likert || [], [questionnaire]);
  const total = questionnaire?.total || statements.length;
  const blockSize = questionnaire?.block_size || 10;

  const [answers, setAnswers] = useState(() => ({ ...(questionnaire?.answers || {}) }));
  // Resume lands on the first unanswered statement, which is what "continue
  // where you left off" means to the person who left off.
  const [index, setIndex] = useState(() => {
    const given = questionnaire?.answers || {};
    const list = questionnaire?.statements || [];
    const first = list.findIndex((statement) => given[statement.id] == null);
    return first === -1 ? Math.max(0, list.length - 1) : first;
  });
  const [onIntro, setOnIntro] = useState(startOnIntro);
  const [busy, setBusy] = useState(false);

  const answeredCount = Object.keys(answers).length;
  const current = statements[Math.min(index, Math.max(0, statements.length - 1))];
  const currentAnswer = current ? answers[current.id] : undefined;
  const blocks = Math.max(1, Math.ceil(total / blockSize));
  const currentBlock = Math.floor(index / blockSize);
  const isLast = index >= statements.length - 1;

  const pick = (value) => {
    if (!current) return;
    const next = { ...answers, [current.id]: value };
    setAnswers(next);
    // Fire and forget: the answer is already on screen, and Pause/Finish resend
    // the whole map, so a dropped write costs nothing.
    coachingPersonalityService.saveAnswers({ [current.id]: value });
  };

  const pause = async () => {
    setBusy(true);
    await coachingPersonalityService.saveAnswers(answers);
    setBusy(false);
    if (onPause) onPause(answeredCount);
  };

  const finish = async () => {
    setBusy(true);
    const result = await coachingPersonalityService.complete(answers);
    setBusy(false);
    if (result.success && onCompleted) onCompleted(result.data);
  };

  if (onIntro) {
    return (
      <div
        dir={rtl ? "rtl" : undefined}
        className="mx-auto w-full max-w-[620px] py-[clamp(16px,4vh,40px)]"
      >
        <div className="bg-white border border-[#E5E7EB] rounded-[14px] p-[clamp(20px,4vw,40px)]">
          {/* The prototype puts an eyebrow reading "Coaching Personality" at the
              top of the intro card, because there it is a screen of its own. In
              the section the heading is already three lines above it, so the
              eyebrow would print the same words twice on one screen. */}
          {/* intro.text is ONE registered string. The prototype splits it into a
              heading and a paragraph; split, neither half is a key and neither
              half would ever be translated. */}
          <p className="mt-[10px] text-[clamp(17px,2.4vw,20px)] font-semibold leading-[1.5] text-[#111827] [text-wrap:pretty]">
            {S.introText}
          </p>
          <p className="mt-4 text-[14px] leading-[1.6] text-[#6B7280]">
            {S.introPrivacy}
          </p>
          <div className="mt-5 pt-4 border-t border-[#E5E7EB] flex flex-wrap items-center gap-x-[10px] gap-y-1 text-[12px] text-[#6B7280]">
            <span>{S.introCount}</span>
            <span aria-hidden="true" className="text-[#E5E7EB]">
              |
            </span>
            <span>{S.tagSelfAssessment}</span>
            <span aria-hidden="true" className="text-[#E5E7EB]">
              |
            </span>
            <span>{S.tagBigFive}</span>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={() => setOnIntro(false)}
              className="min-h-[44px] px-8 rounded-[10px] border-none bg-[#2183FE] text-white text-[15px] font-semibold hover:bg-[#1668D9]"
            >
              {S.start}
            </button>
            <button
              type="button"
              onClick={() => onLeaveIntro && onLeaveIntro()}
              className="min-h-[44px] px-[10px] bg-transparent border-none text-[#6B7280] text-[14px] hover:text-[#111827]"
            >
              {S.notNow}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!current) return null;

  return (
    <div
      dir={rtl ? "rtl" : undefined}
      className="mx-auto w-full max-w-[620px] py-[clamp(12px,3vh,32px)]"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-[10px]">
        {/* ONE text node: the number and the sentence around it are a single
            template key, and a split node would never match it. */}
        <span className="text-[12px] font-semibold text-[#6B7280] min-w-0">
          {S.progress(answeredCount)}
        </span>
        <span className="flex-1" />
        <button type="button" onClick={pause} disabled={busy} className={GHOST}>
          {S.pause}
        </button>
      </div>

      <div className="flex gap-[6px] mb-5">
        {Array.from({ length: blocks }).map((_, position) => (
          <div
            key={position}
            className={`flex-1 h-[4px] rounded-[2px] ${
              answeredCount >= (position + 1) * blockSize
                ? "bg-[#2183FE]"
                : position === currentBlock
                ? "bg-[#BBD8FE]"
                : "bg-[#E5E7EB]"
            }`}
          />
        ))}
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-[14px] p-[clamp(20px,4vw,32px)]">
        <div className="min-h-[96px] flex items-start">
          {/* The statement is never shortened and never clamped (layout rule 4).
              It arrives from the server as the registered English source. */}
          <p className="m-0 text-[clamp(17px,2.4vw,20px)] font-semibold leading-[1.5] text-[#111827] [text-wrap:pretty]">
            {current.text}
          </p>
        </div>
        <div className="flex flex-col gap-2 mt-5">
          {likert.map((option) => {
            const selected = currentAnswer === option.value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => pick(option.value)}
                aria-pressed={selected}
                className={`flex items-center gap-3 w-full text-start px-4 py-3 min-h-[48px] rounded-[10px] text-[15px] text-[#374151] transition-colors ${
                  selected
                    ? "bg-[#E9F2FF] border border-[#2183FE]"
                    : "bg-white border border-[#E5E7EB] hover:border-[#BBD8FE]"
                }`}
              >
                <span
                  className={`w-[18px] h-[18px] rounded-full border-2 box-border flex-none flex items-center justify-center ${
                    selected ? "border-[#2183FE]" : "border-[#C7CCD6]"
                  }`}
                >
                  {selected && (
                    <span className="w-2 h-2 rounded-full bg-[#2183FE]" />
                  )}
                </span>
                {/* Likert labels are meaning texts: never clamped, never cut. */}
                <span className="min-w-0">{option.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-3 mt-4">
        {index > 0 && (
          <button
            type="button"
            onClick={() => setIndex(Math.max(0, index - 1))}
            className={GHOST}
          >
            {S.back}
          </button>
        )}
        <span className="flex-1" />
        <button
          type="button"
          disabled={currentAnswer == null || busy}
          onClick={() => (isLast ? finish() : setIndex(index + 1))}
          className={`${PRIMARY} ${
            currentAnswer == null || busy
              ? "opacity-45 cursor-default"
              : "hover:bg-[#1668D9]"
          }`}
        >
          {isLast ? S.finish : S.next}
        </button>
      </div>
    </div>
  );
};

export default Questionnaire;
